import { useFlags } from 'launchdarkly-react-client-sdk';

// BEFORE CREATING A FLAG, READ FOR BEST PRACTICES:
// https://www.flagship.io/feature-flags-naming-conventions/
// https://docs.launchdarkly.com/guides/best-practices/creating-flags

// Flag naming convention:
// feature_{release/op/experiment/permission}_team_month_year{_temp, if temp}

// Do not use dots in the name, while allowed in LD,
// you cannot access flag with dots in the name in react/js
// We truned off auto camel casing on the provider, more here
// https://docs.launchdarkly.com/sdk/client-side/react/react-web#flag-keys

// Launch Darkly Up Time - Plan your default and failure states well:
// We cannot expect 100% uptime from launch darkly. Plan default values and error handling in implementation
// carefully to plan for cases when we may not be able to reach launch darkly to retrieve the flag value.

// WARNING: Launch Darkly will not have 100% uptime. We cannot guarantee a flag value is returned every time
// or that we will not receive the default flag value. Plan you default and failure states for flags and
// implementation very carefully. Think about, what happens if this flag value returns null, or the default
// specified? How will users be impacted? Manage and design for those cases.

// Type of flags:
// RELEASE: a flag used to roll out a release (enable or disable), will be temporary
// OP: operations flag used to turn on and off operations tooling, may or may not be temporary
// EXPERIMENT: A/B and other testing flags, will likely be temporary
// PERMISSION: flag to control feature permissions, eg Only enterprise users have feature XYZ, will not be temporary

// Usage eg:
// import { useFeatureFlag, FLAGS } from 'utils/launchdarkly/flags'
// const isTestFlagEnabled = useFeatureFlag(FLAGS.TEST);
export function useFeatureFlag(flag) {
  try {
    const flagValue = useFlags()[flag.name];
    return typeof flagValue === 'undefined' ? flag.default : flagValue;
  } catch (err) {
    console.error('error from Launch Darkly', err);
    return flag?.default || false;
  }
}

// For temporary flags, like release flags, it is considered best practice to post a branch with
// the flag removed so that flag cleanup is as easy as posssible.

export const FLAGS = {
  // TEMPLATE: {
  //  name        : 'feature_{release/op/experiment/permission}_team_month_year{_temp, if temp}',
  //  description : 'Feature flag to allow enterprise customers access to enterprise feautres',
  //  default     : false,
  //  values      : [true, false],
  //  useFlag     : () => useFlags().<TEMPLATE.name>,
  // },
  // TEST: {
  //  name        : 'test_mxo_12_21_temp',
  //  description : 'Feature flag to test flagging wrapper',
  //  default     : false,
  //  values      : [true, false],
  //  useFlag     : () => useFlags().test_mxo_12_21_temp,
  // },
  ASSESSMENT_SMEARGLE_REMOVAL: {
    name: 'assessment_smeargle_removal_platform_06_02_temp',
    description: 'Temporary flag to show/hide Assessment Smeargle Removal work',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().assessment_smeargle_removal_platform_06_02_temp,
  },
  ENABLE_FLEX_SESSIONS: {
    name: 'enable_flex_sessions_platform_04_17_temp',
    description: 'Temporary flag to show/hide flex sessions',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().enable_flex_sessions_platform_04_17_temp,
  },
  HIDE_COHORT_NOTES: {
    name: 'feature_hide_cohort_notes_platform_10_31_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_hide_cohort_notes_platform_10_31_temp,
  },
  MEMBER_MERGE: {
    name: 'feature_member_merge_admin_release_cx_5_23_temp',
    description: 'Release feature flag to allow admins to merge members',
    default: 'false',
    values: ['true', 'false'],
    useFlag: () => useFlags().feature_member_merge_admin_release_cx_5_23_temp,
  },
  CANCEL_APPOINTMENT: {
    name: 'appointment_unhappy_path_12_28_temp',
    description: 'Temporary flag to enable/disable the cancel appointment modal.',
    default: 'false',
    values: ['true', 'false'],
    useFlag: () => useFlags().appointment_unhappy_path_12_28_temp,
  },
  MAINTENANCE_MODE: {
    name: 'maintenance_mode_release_pfrm_03_2023_temp',
    description: 'Manually control maintenance polling',
    default: 'false',
    values: ['true', 'false'],
    useFlag: () => useFlags().maintenance_mode_release_pfrm_03_2023_temp,
  },
  FILTER_BY_PROVIDER_TAGS: {
    name: 'release_browse_by_provider_tags_pfrm_2024_05_22_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags()['release_browse_by_provider_tags_pfrm_2024_05_22_temp'],
  },
  RELEASE_NEW_ASSIGN_PROVIDER_MODAL: {
    name: 'release_new_assign_provider_modal_pfrm_2024_06_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags()['release_new_assign_provider_modal_pfrm_2024_06_temp'],
  },
  DEACTIVATIONS: {
    name: 'feature_deactivation_platform_07_24',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_deactivation_platform_07_24,
  },
  SHOW_COMMUNITY_CARE_SOLUTION: {
    name: 'feature_community_care_solution_platform_08_24_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_community_care_solution_platform_08_24_temp,
  },
  SHOW_SPECIALTY_CARE_NAVIGATOR: {
    name: 'speacialty_care_navigator_release_admin_sudbhb_53_2024_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().speacialty_care_navigator_release_admin_sudbhb_53_2024_temp,
  },
  SHOW_PEER_RECOVERY: {
    name: 'peer_recovery_release_admin_sudbhb_07_2024_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().peer_recovery_release_admin_sudbhb_07_2024_temp,
  },
  SHOW_GATED_BOOKING_TOGGLE: {
    name: 'gated_booking_toggle_release_mfopx_09_23_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().gated_booking_toggle_release_mfopx_09_23_temp,
  },
  BAMBOO_SHOW_ADMIN_CONTROLS: {
    name: 'bamboo_show_admin_controls',
    description: 'Enable all new controls for Bamboo Admin',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().bamboo_show_admin_controls,
  },
  BULK_CLONE_PLAN_YEARS: {
    name: 'plan_year_bulk_cloning_core_2024_09_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().plan_year_bulk_cloning_core_2024_09_temp,
  },
  PERMISSIONS_DEACTIVATION_TOGGLE: {
    name: 'permissions_auto_deactivations_toggle_platform_10_2024',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().permissions_auto_deactivations_toggle_platform_10_2024,
  },
  CUSTOMER_CANCELLATION_POLICY_COPY: {
    name: 'feature_customer_cancellation_policy_10_09_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_customer_cancellation_policy_10_09_temp,
  },
  CANCELLATION_REASONS_V2: {
    name: 'admin_cancellation_modal_reasons_11__18_2024',
    description: 'Enables the new cancellation reasons on the cancel modal in the calendar',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().admin_cancellation_modal_reasons_11__18_2024,
  },
  SHOW_SPECIALTY_CARE_PLAN_YEAR_CHECKBOX: {
    name: 'show_specialty_care_plan_year_checkbox_mxsc_11_24',
    description: 'Shows/Hides the new specialty care checkbox',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_specialty_care_plan_year_checkbox_mxsc_11_24,
  },
  MEMBER_COMMUNICATION_DATE: {
    name: 'feature_member_communications_11_26_2024_temp',
    description: 'Member communications date picker toggle on customer',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_member_communications_11_26_2024_temp,
  },
  RELEASE_NEW_MEMBER_MOVER: {
    name: 'release_new_member_move_tool_pfrm_2024-12',
    default: false,
    values: [true, false],
    useFlag: () => useFlags()['release_new_member_move_tool_pfrm_2024-12'],
  },
  LINK_PRIMARY_COVERED_LIFE: {
    name: 'feature_link_primary_covered_life_modal_12_24',
    default: false,
    values: [true, false],
    useFlag: () => useFlags()['feature_link_primary_covered_life_modal_12_24'],
  },
  SESSION_ALLOCATIONS_REWRITE: {
    name: 'feature_revamp_appointment_allocations_01_25_platform_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_revamp_appointment_allocations_01_25_platform_temp,
  },
  CHANNEL_PARTNER_UPDATE: {
    name: 'feature_channel_partner_ui_redesign_02_25_platform_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_channel_partner_ui_redesign_02_25_platform_temp,
  },
  INSURANCE_SIGN_UP: {
    name: 'insurance_sign_up_rcm_03_25_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().insurance_sign_up_rcm_03_25_temp,
  },
  CUSTOMER_RATE_CARDS: {
    name: 'show_customer_rate_cards_rcm_03_2025_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_customer_rate_cards_rcm_03_2025_temp,
    // Removal task: https://springhealth.atlassian.net/browse/RCM-1788
  },
  REMOVE_NO_NEW_PATIENTS: {
    name: 'remove_no_new_patients',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().remove_no_new_patients,
  },
  PROMISE_ID_DISABLED: {
    name: 'disable_promise_id_editing_4_16_2025',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().disable_promise_id_editing_4_16_2025,
  },
  REDESIGN_CAREGIVER_PROFILE: {
    name: 'feature_provider_redesign_platform_01_24_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_provider_redesign_platform_01_24_temp,
  },
  PROVIDER_LICENSES_READ_ONLY: {
    name: 'provider_licenses_read_only_cfpl_2608_temp_2026_12',
    description: 'Makes provider license fields read-only in Admin Portal',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().provider_licenses_read_only_cfpl_2608_temp_2026_12,
  },
  EDIT_CENSUS_INGESTIBLE_TYPE: {
    name: 'can_edit_census_ingestible_type_4_29_25_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_edit_census_ingestible_type_4_29_25_temp,
  },
  VIDEO_CALL_TAB: {
    name: 'show_video_call_history_tab_04_25_cgxp_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_video_call_history_tab_04_25_cgxp_temp,
  },
  CREATE_MANUAL_COVERED_LIFE: {
    name: 'can_create_manual_covered_lives_from_errored_05_05_25',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_create_manual_covered_lives_from_errored_05_05_25,
  },
  CAN_EDIT_COVERED_LIFE_CORPORATE_EMAIL: {
    name: 'can_edit_covered_life_corporate_email_05_27_25_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_edit_covered_life_corporate_email_05_27_25_temp,
  },
  OFFSHORE_SUPPORT_ENABLED: {
    name: 'display_offshore_support_2025_06_core_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().display_offshore_support_2025_06_core_temp,
  },
  CAN_EDIT_PAYER_COVERED_LIFE_COUNTS: {
    name: 'can_edit_payer_covered_life_counts_07_07_25_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_edit_payer_covered_life_counts_07_07_25_temp,
  },
  CAN_ALLOW_COVERED_LIVES_TRANSFER: {
    name: 'can_allow_existing_covered_lives_to_move_in_from_customer_ids_temp_6_27_25',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_allow_existing_covered_lives_to_move_in_from_customer_ids_temp_6_27_25,
  },
  COHORT_PAYER_MAPPINGS_ENABLED: {
    name: 'cohort_payer_mappings_release_rcm_07_2025_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().cohort_payer_mappings_release_rcm_07_2025_temp,
  },
  BULK_MEMBER_CHARGES_ENABLED: {
    name: 'bulk_member_charges_rev_2026_04_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().bulk_member_charges_rev_2026_04_temp,
  },
  CAN_MANUALLY_UPLOAD_CENSUS_FILES: {
    name: 'can_manually_upload_census_files_temp_09_2025_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().can_manually_upload_census_files_temp_09_2025_temp,
  },
  MANAGER_EX_CENSUS_RELEASE_CXSW_11_2025_TEMP: {
    name: 'manager_ex_census_release_cxsw_11_2025_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().manager_ex_census_release_cxsw_11_2025_temp,
  },
  SHOW_ACL_TAB: {
    name: 'show_acl_tab',
    description: 'Feature flag to show/hide ACL tab in Users page',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_acl_tab_11_25_cgxp_temp,
  },
  CANCELLATION_REASONS_V3: {
    name: 'appointment_cancellation_form_v3_cgxps_1474',
    description: 'Enables the new cancellation form on the appointment cancel modal',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().appointment_cancellation_form_v3_cgxps_1474,
  },
  DOMESTIC_CN_FILTER_CARE_TEAM: {
    name: 'domestic_cn_filter_care_team_01_20_temp',
    description: 'Enables the new cancellation form on the appointment cancel modal',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().domestic_cn_filter_care_team_01_20_temp,
  },
  SHOW_REGIONS_TAB: {
    name: 'show_regions_tab_globile_05_26_temp',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_regions_tab_globile_05_26_temp,
  },
  SHOW_BENEFITS_PAGE_TAB: {
    name: 'show_benefits_page_tab_mgsc_09_26',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_benefits_page_tab_mgsc_09_26,
  },
  MEMBER_ADDITIONAL_SESSIONS: {
    name: 'feature_member_additional_sessions_11_18_26_temp',
    description: 'Feature flag to show/hide member additional sessions',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_member_additional_sessions_11_18_26_temp,
  },
  COACHING_BENEFIT_IMPROVEMENTS_ENABLED: {
    name: 'enable_coaching_benefit_improvements_mg_payer_02_26_temp',
    description: 'Feature flag to enable coaching benefit improvements',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().enable_coaching_benefit_improvements_mg_payer_02_26_temp,
  },
  MEMBER_PROFILE_REORGANIZATION: {
    name: 'feature_member_profile_reorg_01_26_temp',
    description: 'Feature flag to show/hide member profile reorganization',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_member_profile_reorg_01_26_temp,
  },
  MULTI_ELIGIBILITY_MEMBERSHIPS: {
    name: 'feature_multi_eligibility_memberships_2026_04_temp',
    description: 'Temporary flag for multi-eligibility memberships card on member profile',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_multi_eligibility_memberships_2026_04_temp,
  },
  CHANNEL_PARTNER_UPLOADS_TAB: {
    name: 'show_channel_partners_uploads_tab_pit_2026_03_temp',
    description: 'Feature flag to show/hide channel partner uploads tab',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_channel_partners_uploads_tab_pit_2026_03_temp,
  },
  PROVIDER_BROWSE_ROUTES_REDESIGN: {
    name: 'provider_browse_routes_redesign_2026_03_temp',
    description: 'Feature flag to show/hide redesigned provider browse routes',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().provider_browse_routes_redesign_2026_03_temp,
  },
  RELEASE_NEW_CARE_PROVIDER_SCHEDULE_MODAL: {
    name: 'release_new_care_provider_schedule_modal_pit_2026_06_15',
    description: 'Release flag to swap the smeargle Form in CareProviderScheduleModal for AppointmentModalForm',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().release_new_care_provider_schedule_modal_pit_2026_06_15,
  },
  RELEASE_NEW_TRIAL_FORM: {
    name: 'release_new_trial_form_2026_07_07',
    description: 'Release flag to swap the legacy smeargle TrialForm for the react-hook-form based CreateTrialForm',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().release_new_trial_form_2026_07_07,
  },
  SHOW_PAYER_RULES_TAB: {
    name: 'payer_rules_rcm_07_2026_temp',
    description: 'Feature flag to show/hide the Payer Rules tab on the Revenue Cycle page',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().payer_rules_rcm_07_2026_temp,
  },
  SHOW_STRUCTURED_BIO: {
    name: 'cfpl_provider_bio_mvp_072026_temp',
    description: 'Show approved/live structured provider bio (CFPL-2119)',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().cfpl_provider_bio_mvp_072026_temp,
  },
  SHOW_DEPENDENT_COHORT_FIELD: {
    name: 'feature_dependent_cohort_field_customers_07_26_temp',
    description: 'Temporary flag to show the Dependent Cohort field for customers beyond Highmark (e.g. ByteDance)',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_dependent_cohort_field_customers_07_26_temp,
  },
  DEDICATED_PROVIDER_IMPLEMENTATION: {
    name: 'dedicated_provider_implementation_wpc_2026_temp',
    description: 'Temporary flag to show/hide the read-only Dedicated Provider Implementation tab (WPC-1043)',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().dedicated_provider_implementation_wpc_2026_temp,
  },
  MEMBER_DETAILS_FORM_REDESIGN: {
    name: 'display_new_member_details_form_pitt_10_2026_temp',
    description: 'Temporary flag to show the Verdant/Chakra EditMemberDetailForm instead of the smeargle one',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().display_new_member_details_form_pitt_10_2026_temp,
  },
  RELEASE_NEW_BROWSE_COACH: {
    name: 'release_new_browse_coach_pit_2026_10_temp',
    description: 'Releases the new browse UI on the Coach browse route',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().release_new_browse_coach_pit_2026_10_temp,
  },
  AUTH0_ADMIN_PORTAL_LOGIN: {
    name: 'auth0_admin_portal_login_2026_temp',
    description: 'Switches Admin Portal sign-in, logout and session hydration from Diglet to Auth0 globally (PCSP-157)',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().auth0_admin_portal_login_2026_temp,
  },
  SHOW_INSURANCE_SNAPSHOT: {
    name: 'show_insurance_snapshot_admin_2026_10_temp',
    description: 'Shows the Insurance Snapshot card for US members on the member page',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().show_insurance_snapshot_admin_2026_10_temp,
  },
  COVERAGE_AWARE_SESSION_MANAGEMENT: {
    name: 'feature_coverage_aware_session_management_10_26_temp',
    description:
      'Temporary flag to show the new MemberVisitHistory instead of VisitUsageTable on the member Appointments tab',
    default: false,
    values: [true, false],
    useFlag: () => useFlags().feature_coverage_aware_session_management_10_26_temp,
  },
};
